// Routine / collection TYPE-slot recognizers — the two data-type positions
// that are not a column definition: a routine header's declared return type
// and a PL/SQL collection's element type. Split out of
// `editorContextSlotShared.ts` (the DDL unknown-position sweep pushed that
// file past the ~200-line budget) along a clean seam: both answer "the caret
// takes a TYPE" from a KEYWORD tail rather than from an enclosing paren, so
// neither shares any internal with the column-definition shapes that stayed
// behind. G11: pure token scan, NO web-tree-sitter.
//
// `isDataTypeTargetSlot` (the shared dispatcher) calls both. The import back
// into `editorContextSlotShared` for the scan primitives mirrors the existing
// `editorContextSlot.ddl` ↔ shared pair — function declarations only, so the
// cycle is resolved at call time.

import type { ClauseToken } from './editorContextClause';
import { findEnclosingLparenIndex, upper } from './editorContextSlotShared';

/**
 * The declared RETURN TYPE of a routine header:
 *
 *   `CREATE FUNCTION f(…) RETURNS ▮`   — PG / MySQL / T-SQL
 *   `CREATE FUNCTION f(…) RETURN ▮`    — Oracle PL/SQL
 *
 * `RETURNS` is unambiguous: no dialect uses the word anywhere else, so a bare
 * `RETURNS` tail inside a routine header is always a type slot. Oracle's
 * `RETURN` is NOT — it is also the PL/SQL statement that returns a VALUE
 * (`RETURN SELF.amount;` inside a member function body), where the type
 * vocabulary would be exactly wrong. Two guards keep the two apart: the
 * keyword must close the parameter list (its previous token is the header's
 * `)`, or the routine's own name for a parameterless function), and no `BEGIN`
 * may precede the caret — every `RETURN <expr>` statement lives inside one.
 */
export function isRoutineReturnTypeSlot(tokens: readonly ClauseToken[]): boolean {
  if (findEnclosingLparenIndex(tokens) !== -1) return false;
  const i = tokens.length - 1;
  const kw = upper(tokens[i]);
  if (kw !== 'RETURNS' && kw !== 'RETURN') return false;
  if (!headIsCreateRoutine(tokens)) return false;
  if (kw === 'RETURNS') return true;
  // Oracle's bare `RETURN`: the header's parameter list must close directly
  // before it (or, for a no-arg function, the routine name), and the caret
  // must still be OUTSIDE the routine body.
  if (tokens.some((t) => upper(t) === 'BEGIN')) return false;
  const before = tokens[i - 1];
  if (!before) return false;
  return before.kind === 'rparen' || before.kind === 'identifier';
}

/** True when a `CREATE [OR REPLACE] … FUNCTION|PROCEDURE` head precedes the
 *  caret. `FUNCTION` may be preceded by `MEMBER` / `STATIC` (Oracle object
 *  types) and by the usual OR/REPLACE/EDITIONABLE noise. */
function headIsCreateRoutine(tokens: readonly ClauseToken[]): boolean {
  return tokens.some((t) => {
    const u = upper(t);
    return u === 'FUNCTION' || u === 'PROCEDURE';
  });
}

/**
 * Oracle's collection-type element slot — `TYPE t IS TABLE OF ▮` and
 * `TYPE t IS VARRAY(n) OF ▮`. The `IS` before `TABLE`/`VARRAY` is what
 * separates this from a trigger's `UPDATE OF ▮` column list (owned by the DDL
 * family's trigger slot) and from `GRANT … ON ▮`-style `OF`-free shapes.
 *
 * PL/SQL is the only one of the five dialects with a declared collection type,
 * so the shape is unreachable in the other four.
 */
export function isCollectionElementTypeSlot(tokens: readonly ClauseToken[]): boolean {
  let i = tokens.length - 1;
  if (upper(tokens[i]) !== 'OF') return false;
  i -= 1;
  // `VARRAY(n) OF ▮` — step back over the size argument.
  if (tokens[i]?.kind === 'rparen') {
    let depth = 0;
    for (; i >= 0; i -= 1) {
      const t = tokens[i]!;
      if (t.kind === 'rparen') depth += 1;
      else if (t.kind === 'lparen') {
        depth -= 1;
        if (depth === 0) {
          i -= 1;
          break;
        }
      }
    }
  }
  const collection = upper(tokens[i]);
  if (collection !== 'TABLE' && collection !== 'VARRAY') return false;
  return upper(tokens[i - 1]) === 'IS';
}
